import React from 'react';
import Style from './Governance.module.css';
import ConfirmPopup from '../common/popover/confirmPopup';

// ─────────────────────────────────────────────────────────────────────────────
// GovernanceLifecycleModals — the Cancel / Submit-for-Approval / Reject / Publish
// confirmations + the swallowed-error dialog, shared by both editors. Driven
// entirely by the state and handlers returned from useGovernanceLifecycle. Only
// the reject-reason placeholder and the publish blurb differ per control type;
// both are passed in as copy.
// ─────────────────────────────────────────────────────────────────────────────
const GovernanceLifecycleModals = ({
  popup, setPopup,
  rejectReason, setRejectReason,
  errorPopup, setErrorPopup,
  isBusy, publishing,
  onCancelConfirmed, onApproveConfirmed, onRejectConfirmed, onPublishConfirmed,
  rejectPlaceholder, publishDescription,
}) => (
  <>
    {/* Cancel warns about unsaved edits, does NOT discard the draft. */}
    {popup === 'cancel' && (
      <ConfirmPopup
        details={{
          icon: '', type: 'warning', title: 'Unsaved Edits',
          description: 'Any unsaved edits will be lost. Continue?',
          buttons: 'Y,N',
        }}
        onYesClick={onCancelConfirmed}
        onNoClick={() => setPopup(null)}
      />
    )}

    {/* Submit for Approval */}
    {popup === 'approve' && (
      <ConfirmPopup
        details={{
          icon: '', type: 'info', title: 'Submit for Approval?',
          description: 'This will mark the draft as approved and ready to publish. Continue?',
          buttons: 'Y,N',
        }}
        onYesClick={onApproveConfirmed}
        onNoClick={() => setPopup(null)}
      />
    )}

    {/* Reject — inline modal to collect the rejection reason */}
    {popup === 'reject' && (
      <div className={Style.modalOverlay} onMouseDown={() => setPopup(null)}>
        <div className={Style.modal} onMouseDown={(e) => e.stopPropagation()}>
          <div className={Style.modalHeader}>Reject Draft</div>
          <div className={Style.modalBody}>
            <p style={{ marginTop: 0, marginBottom: 14, fontSize: 13.5, color: '#374151' }}>
              Provide a reason for rejection. The draft will return to an editable state.
            </p>
            <div className={Style.formGroup}>
              <label className={Style.label}>Rejection Reason</label>
              <textarea
                className={Style.textarea}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={rejectPlaceholder}
              />
            </div>
          </div>
          <div className={Style.modalFooter}>
            <button className={Style.btn} onClick={() => setPopup(null)} disabled={isBusy}>Cancel</button>
            <button
              className={`${Style.btn} ${Style.btnPrimary}`}
              style={{ background: '#dc2626', borderColor: '#dc2626' }}
              onClick={onRejectConfirmed}
              disabled={isBusy}
            >
              {publishing ? 'Rejecting…' : 'Reject Draft'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Publish confirmation */}
    {popup === 'publish' && (
      <ConfirmPopup
        details={{
          icon: '', type: 'info', title: 'Publish Policy?',
          description: publishDescription,
          buttons: 'Y,N',
        }}
        onYesClick={onPublishConfirmed}
        onNoClick={() => setPopup(null)}
      />
    )}

    {/* Surfaces any failed save / approve / reject / publish so a swallowed API
        error is no longer mistaken for success. */}
    {errorPopup && (
      <ConfirmPopup
        details={{
          icon: '', type: 'error', title: 'Something went wrong',
          description: errorPopup, buttons: 'O',
        }}
        onCloseClick={() => setErrorPopup(null)}
      />
    )}
  </>
);

export default GovernanceLifecycleModals;
